import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import type { KeywordRow, SearchParams } from "./_types";
import {
  ALL_CANDIDATE_TYPES, FIRM_TIER_OPTIONS, HONORS, KEYWORD_FIELDS,
  KEYWORD_OPERATORS, PRACTICE_AREAS, SCHOOL_TIER_OPTIONS, SPECIALTIES,
} from "../../data/leopard_options";
import { type LocationNode, LOCATION_TREE } from "../../data/leopard_locations";

interface Props {
  open:         boolean;
  searchParams: SearchParams;
  onSave:       (updated: SearchParams) => void;
  onClose:      () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function SectionHeader({ label, badge }: { label: string; badge?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide shrink-0">{label}</span>
      {badge}
      <div className="flex-1 h-px bg-gray-100" />
    </div>
  );
}

// task 446ea38b Slice 2 — labels for LeopardSpecialtyResolver's provenance
// so Steven can tell an AI-derived filter apart from one he set himself.
const SPECIALTY_SOURCE_LABELS: Record<string, string> = {
  alias: "AI-matched",
  ai:    "AI-matched",
  miss:  "No AI match — broad search",
};

function ProvenanceBadge({ label, warn = false }: { label?: string; warn?: boolean }) {
  if (!label) return null;
  const tone = warn ? "bg-amber-50 text-amber-700" : "bg-green-50 text-green-700";
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${tone}`}>{label}</span>;
}

function OperatorToggle({ value, onChange }: { value: "OR" | "AND"; onChange: (v: "OR" | "AND") => void }) {
  return (
    <div className="flex rounded border border-gray-200 overflow-hidden text-xs">
      {(["OR", "AND"] as const).map((op) => (
        <button
          key={op}
          onClick={() => onChange(op)}
          className={`px-2.5 py-1 ${value === op ? "bg-blue-50 text-blue-700 font-medium" : "bg-white text-gray-400"} ${op === "AND" ? "border-l border-gray-200" : ""}`}
        >
          {op}
        </button>
      ))}
    </div>
  );
}

// Multiselect with include / exclude tabs and optional search
function IncludeExcludePanel({
  options,
  included,
  excluded,
  onChange,
  searchable = false,
  operator,
  onOperatorChange,
}: {
  options:           string[];
  included:          string[];
  excluded:          string[];
  onChange:          (inc: string[], exc: string[]) => void;
  searchable?:       boolean;
  operator?:         "OR" | "AND";
  onOperatorChange?: (v: "OR" | "AND") => void;
}) {
  const [mode, setMode]     = useState<"include" | "exclude">("include");
  const [query, setQuery]   = useState("");

  const filtered = useMemo(
    () => query ? options.filter((o) => o.toLowerCase().includes(query.toLowerCase())) : options,
    [options, query]
  );

  function toggle(item: string) {
    if (mode === "include") {
      const next = included.includes(item) ? included.filter((i) => i !== item) : [...included, item];
      onChange(next, excluded);
    } else {
      const next = excluded.includes(item) ? excluded.filter((i) => i !== item) : [...excluded, item];
      onChange(included, next);
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="flex rounded border border-gray-200 overflow-hidden text-xs">
          <button
            onClick={() => setMode("include")}
            className={`px-3 py-1 ${mode === "include" ? "bg-green-50 text-green-700 font-medium" : "bg-white text-gray-400"}`}
          >
            Include{included.length > 0 ? ` (${included.length})` : ""}
          </button>
          <button
            onClick={() => setMode("exclude")}
            className={`px-3 py-1 border-l border-gray-200 ${mode === "exclude" ? "bg-red-50 text-red-700 font-medium" : "bg-white text-gray-400"}`}
          >
            Exclude{excluded.length > 0 ? ` (${excluded.length})` : ""}
          </button>
        </div>
        {operator !== undefined && onOperatorChange && (
          <div className="ml-auto">
            <OperatorToggle value={operator} onChange={onOperatorChange} />
          </div>
        )}
      </div>

      {searchable && (
        <input
          type="text"
          placeholder="Search..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full mb-2 text-sm border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-600"
        />
      )}

      <div className="max-h-44 overflow-y-auto border border-gray-100 rounded bg-gray-50 p-1.5 space-y-0.5">
        {filtered.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-3">No options match</p>
        )}
        {filtered.map((item) => {
          const isInc = included.includes(item);
          const isExc = excluded.includes(item);
          const active   = mode === "include" ? isInc : isExc;
          const opposite = mode === "include" ? isExc : isInc;
          return (
            <label
              key={item}
              className={`flex items-center gap-2 text-xs px-1.5 py-0.5 rounded cursor-pointer hover:bg-white select-none ${opposite ? "opacity-30 cursor-not-allowed" : ""}`}
            >
              <input
                type="checkbox"
                checked={active}
                onChange={() => !opposite && toggle(item)}
                disabled={opposite}
                className="rounded border-gray-300 accent-green-700"
              />
              <span className={active ? (mode === "include" ? "text-green-700 font-medium" : "text-red-600 font-medium") : "text-gray-700"}>
                {item}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section components — each reads/writes draft via setDraft helper
// ---------------------------------------------------------------------------

function LocationsSection({ draft, update }: { draft: SearchParams; update: (patch: Partial<SearchParams>) => void }) {
  const [mode, setMode]         = useState<"include" | "exclude">("include");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [query, setQuery]       = useState("");

  const incStates      = draft.locations?.include?.states       ?? [];
  const excStates      = draft.locations?.exclude?.states       ?? [];
  const incStateCities = draft.locations?.include?.state_cities ?? [];
  const excStateCities = draft.locations?.exclude?.state_cities ?? [];
  const incCities      = draft.locations?.include?.cities       ?? [];
  const excCities      = draft.locations?.exclude?.cities       ?? [];

  function commit(iS: string[], iSC: string[], iC: string[], eS: string[], eSC: string[], eC: string[]) {
    update({
      locations: {
        include: { states: iS, state_cities: iSC, cities: iC },
        exclude: { states: eS, state_cities: eSC, cities: eC },
      },
    });
  }

  function toggleExpand(key: string) {
    setExpanded((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }

  function toggleState(name: string) {
    if (mode === "include") {
      const n = incStates.includes(name) ? incStates.filter(s => s !== name) : [...incStates, name];
      commit(n, incStateCities, incCities, excStates, excStateCities, excCities);
    } else {
      const n = excStates.includes(name) ? excStates.filter(s => s !== name) : [...excStates, name];
      commit(incStates, incStateCities, incCities, n, excStateCities, excCities);
    }
  }

  function toggleSubNode(stateName: string, nodeName: string) {
    const isInter = stateName === "INTERNATIONAL";
    const key     = isInter ? nodeName : `${stateName};${nodeName}`;

    if (isInter) {
      if (mode === "include") {
        const n = incCities.includes(key) ? incCities.filter(c => c !== key) : [...incCities, key];
        commit(incStates, incStateCities, n, excStates, excStateCities, excCities);
      } else {
        const n = excCities.includes(key) ? excCities.filter(c => c !== key) : [...excCities, key];
        commit(incStates, incStateCities, incCities, excStates, excStateCities, n);
      }
    } else {
      if (mode === "include") {
        const n = incStateCities.includes(key) ? incStateCities.filter(c => c !== key) : [...incStateCities, key];
        commit(incStates, n, incCities, excStates, excStateCities, excCities);
      } else {
        const n = excStateCities.includes(key) ? excStateCities.filter(c => c !== key) : [...excStateCities, key];
        commit(incStates, incStateCities, incCities, excStates, n, excCities);
      }
    }
  }

  function isStateActive(name: string)   { return (mode === "include" ? incStates : excStates).includes(name); }
  function isStateOpp(name: string)      { return (mode === "include" ? excStates : incStates).includes(name); }

  function isSubActive(stateName: string, nodeName: string) {
    const isInter = stateName === "INTERNATIONAL";
    const key = isInter ? nodeName : `${stateName};${nodeName}`;
    return (mode === "include" ? (isInter ? incCities : incStateCities) : (isInter ? excCities : excStateCities)).includes(key);
  }

  function isSubOpp(stateName: string, nodeName: string) {
    const isInter = stateName === "INTERNATIONAL";
    const key = isInter ? nodeName : `${stateName};${nodeName}`;
    return (mode === "include" ? (isInter ? excCities : excStateCities) : (isInter ? incCities : incStateCities)).includes(key);
  }

  function renderNode(stateName: string, node: LocationNode, pathKey: string): React.ReactNode {
    const hasKids  = (node.children?.length ?? 0) > 0;
    const isExp    = expanded.has(pathKey);
    const active   = isSubActive(stateName, node.name);
    const opposite = isSubOpp(stateName, node.name);
    const isMeta   = node.name.endsWith("*");

    return (
      <div key={pathKey}>
        <div className={`flex items-center gap-1 px-1 py-0.5 rounded hover:bg-white ${opposite ? "opacity-30" : ""}`}>
          {hasKids
            ? <button onClick={() => toggleExpand(pathKey)} className="text-gray-400 hover:text-gray-600 shrink-0">{isExp ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}</button>
            : <span className="w-3 shrink-0" />}
          <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none flex-1 min-w-0">
            <input type="checkbox" checked={active} onChange={() => !opposite && toggleSubNode(stateName, node.name)} disabled={opposite}
              className="rounded border-gray-300 accent-green-700 shrink-0" />
            <span className={`truncate ${active ? (mode === "include" ? "text-green-700 font-medium" : "text-red-600 font-medium") : "text-gray-700"} ${isMeta ? "italic" : ""}`}>
              {node.name}
            </span>
            {isMeta && <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-200 px-1 py-px rounded-sm leading-none shrink-0">metro</span>}
          </label>
        </div>
        {hasKids && isExp && (
          <div className="ml-4 space-y-0.5">
            {node.children!.map(child => renderNode(stateName, child, `${pathKey}::${child.name}`))}
          </div>
        )}
      </div>
    );
  }

  const visibleStates = useMemo(() => {
    if (!query) return LOCATION_TREE;
    const q = query.toLowerCase();
    return LOCATION_TREE.filter(s =>
      s.name.toLowerCase().includes(q) ||
      s.children?.some(c => c.name.toLowerCase().includes(q) || c.children?.some(gc => gc.name.toLowerCase().includes(q)))
    );
  }, [query]);

  const incTotal = incStates.length + incStateCities.length + incCities.length;
  const excTotal = excStates.length + excStateCities.length + excCities.length;

  return (
    <div>
      <SectionHeader label="Locations" />

      <div className="flex items-center gap-2 mb-2">
        <div className="flex rounded border border-gray-200 overflow-hidden text-xs">
          <button onClick={() => setMode("include")}
            className={`px-3 py-1 ${mode === "include" ? "bg-green-50 text-green-700 font-medium" : "bg-white text-gray-400"}`}>
            Include{incTotal > 0 ? ` (${incTotal})` : ""}
          </button>
          <button onClick={() => setMode("exclude")}
            className={`px-3 py-1 border-l border-gray-200 ${mode === "exclude" ? "bg-red-50 text-red-700 font-medium" : "bg-white text-gray-400"}`}>
            Exclude{excTotal > 0 ? ` (${excTotal})` : ""}
          </button>
        </div>
      </div>

      <input type="text" placeholder="Search states or cities..." value={query} onChange={e => setQuery(e.target.value)}
        className="w-full mb-2 text-sm border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-600"
      />

      <div className="max-h-44 overflow-y-auto border border-gray-100 rounded bg-gray-50 p-1.5 space-y-0.5">
        {visibleStates.length === 0 && <p className="text-xs text-gray-400 text-center py-3">No locations match</p>}
        {visibleStates.map(state => {
          const hasKids  = (state.children?.length ?? 0) > 0;
          const isExp    = expanded.has(state.name);
          const active   = isStateActive(state.name);
          const opposite = isStateOpp(state.name);
          return (
            <div key={state.name}>
              <div className={`flex items-center gap-1 px-1 py-0.5 rounded hover:bg-white ${opposite ? "opacity-30" : ""}`}>
                {hasKids
                  ? <button onClick={() => toggleExpand(state.name)} className="text-gray-400 hover:text-gray-600 shrink-0">{isExp ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}</button>
                  : <span className="w-3 shrink-0" />}
                <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none flex-1">
                  <input type="checkbox" checked={active} onChange={() => !opposite && toggleState(state.name)} disabled={opposite}
                    className="rounded border-gray-300 accent-green-700 shrink-0" />
                  <span className={active ? (mode === "include" ? "text-green-700 font-medium" : "text-red-600 font-medium") : "text-gray-700"}>
                    {state.name}
                  </span>
                </label>
              </div>
              {hasKids && isExp && (
                <div className="ml-4 space-y-0.5">
                  {state.children!.map(child => renderNode(state.name, child, `${state.name}::${child.name}`))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PracticeAreaSection({ draft, update }: { draft: SearchParams; update: (patch: Partial<SearchParams>) => void }) {
  const inc = draft.practice_area?.include ?? [];
  const exc = draft.practice_area?.exclude ?? [];
  const op  = (draft.practice_area?.operator ?? "OR") as "OR" | "AND";

  function onChange(inc: string[], exc: string[]) {
    update({ practice_area: { include: inc, exclude: exc, operator: op } });
  }

  function onOp(op: "OR" | "AND") {
    update({ practice_area: { include: inc, exclude: exc, operator: op } });
  }

  return (
    <div>
      <SectionHeader label="Practice Area" />
      <IncludeExcludePanel options={PRACTICE_AREAS} included={inc} excluded={exc} onChange={onChange} operator={op} onOperatorChange={onOp} />
    </div>
  );
}

function SpecialtiesSection({ draft, update }: { draft: SearchParams; update: (patch: Partial<SearchParams>) => void }) {
  const inc = draft.specialties?.include ?? [];
  const exc = draft.specialties?.exclude ?? [];
  const op  = (draft.specialties?.operator ?? "OR") as "OR" | "AND";

  function onChange(inc: string[], exc: string[]) {
    update({ specialties: { include: inc, exclude: exc, operator: op } });
  }

  function onOp(op: "OR" | "AND") {
    update({ specialties: { include: inc, exclude: exc, operator: op } });
  }

  const source = draft.specialty_resolution?.source;

  return (
    <div>
      <SectionHeader
        label="Specialties"
        badge={<ProvenanceBadge label={SPECIALTY_SOURCE_LABELS[source ?? ""]} warn={source === "miss"} />}
      />
      <IncludeExcludePanel options={SPECIALTIES} included={inc} excluded={exc} onChange={onChange} operator={op} onOperatorChange={onOp} searchable />
    </div>
  );
}

function KeywordSection({ draft, update }: { draft: SearchParams; update: (patch: Partial<SearchParams>) => void }) {
  const rows = draft.keyword?.rows ?? [];
  const op   = (draft.keyword?.operator ?? "AND") as "AND" | "OR";

  function setRows(next: KeywordRow[]) {
    update({ keyword: { rows: next, operator: op } });
  }

  function setOp(next: "AND" | "OR") {
    update({ keyword: { rows, operator: next } });
  }

  function addRow() {
    setRows([...rows, { field: "biography", op: "contains", text: "" }]);
  }

  function updateRow(idx: number, patch: Partial<KeywordRow>) {
    setRows(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function removeRow(idx: number) {
    setRows(rows.filter((_, i) => i !== idx));
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <SectionHeader label="Keyword" />
        <div className="ml-auto shrink-0">
          <OperatorToggle value={op} onChange={(v) => setOp(v as "AND" | "OR")} />
        </div>
      </div>

      {rows.length === 0 && (
        <p className="text-xs text-gray-400 mb-2">No keyword filters. Add a row to search within candidate profiles.</p>
      )}

      <div className="space-y-2 mb-2">
        {rows.map((row, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <select
              value={row.field}
              onChange={(e) => updateRow(idx, { field: e.target.value })}
              className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white"
            >
              {KEYWORD_FIELDS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            <select
              value={row.op}
              onChange={(e) => updateRow(idx, { op: e.target.value })}
              className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white"
            >
              {KEYWORD_OPERATORS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <input
              type="text"
              value={row.text}
              onChange={(e) => updateRow(idx, { text: e.target.value })}
              placeholder="value"
              className="flex-1 text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-600"
            />
            <button onClick={() => removeRow(idx)} className="text-gray-400 hover:text-red-500 shrink-0">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      <button onClick={addRow} className="flex items-center gap-1 text-xs text-green-700 hover:text-green-800 font-medium">
        <Plus className="w-3.5 h-3.5" /> Add row
      </button>
    </div>
  );
}

function CandidateTypesSection({ draft, update }: { draft: SearchParams; update: (patch: Partial<SearchParams>) => void }) {
  const types = draft.candidate_types ?? [];

  function toggle(t: string) {
    const next = types.includes(t) ? types.filter((x) => x !== t) : [...types, t];
    update({ candidate_types: next });
  }

  return (
    <div>
      <SectionHeader label="Candidate Types" />
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {ALL_CANDIDATE_TYPES.map((t) => (
          <label key={t} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
            <input type="checkbox" checked={types.includes(t)} onChange={() => toggle(t)} className="rounded border-gray-300 accent-green-700" />
            {t}
          </label>
        ))}
      </div>
    </div>
  );
}

function JdYearSection({ draft, update }: { draft: SearchParams; update: (patch: Partial<SearchParams>) => void }) {
  const jdMin = draft.jd_year?.min ?? "";
  const jdMax = draft.jd_year?.max ?? "";

  function setField(field: "min" | "max", val: string) {
    const num = parseInt(val, 10);
    update({ jd_year: { ...(draft.jd_year ?? { min: null, max: null }), [field]: isNaN(num) ? null : num } });
  }

  return (
    <div>
      <SectionHeader
        label="JD Year"
        badge={<ProvenanceBadge label={draft.jd_year_source === "ai_widened" ? "AI-widened ±2yr" : undefined} />}
      />
      <div className="flex items-center gap-3">
        <div>
          <div className="text-xs text-gray-400 mb-1">Oldest grad year</div>
          <input
            type="number"
            value={jdMin}
            onChange={(e) => setField("min", e.target.value)}
            placeholder="e.g. 2018"
            className="w-28 text-sm border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-600"
          />
        </div>
        <span className="text-gray-400 mt-5">–</span>
        <div>
          <div className="text-xs text-gray-400 mb-1">Newest grad year</div>
          <input
            type="number"
            value={jdMax}
            onChange={(e) => setField("max", e.target.value)}
            placeholder="e.g. 2023"
            className="w-28 text-sm border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-600"
          />
        </div>
      </div>
    </div>
  );
}

function TenureSection({ draft, update }: { draft: SearchParams; update: (patch: Partial<SearchParams>) => void }) {
  const tenure = draft.tenure_min ?? 0;

  return (
    <div>
      <SectionHeader label="Min Tenure" />
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">&gt;</span>
        <input
          type="number"
          min={0}
          value={tenure}
          onChange={(e) => update({ tenure_min: parseInt(e.target.value, 10) || 0 })}
          className="w-20 text-sm border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-600"
        />
        <span className="text-sm text-gray-500">months</span>
      </div>
    </div>
  );
}

function FirmTierSection({ draft, update }: { draft: SearchParams; update: (patch: Partial<SearchParams>) => void }) {
  const tier = draft.firm_tier;

  return (
    <div>
      <SectionHeader label="Firm Tier" />
      <div className="flex gap-4">
        <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
          <input
            type="radio"
            name="firm_tier"
            checked={tier == null}
            onChange={() => update({ firm_tier: null })}
            className="accent-green-700"
          />
          Any
        </label>
        {FIRM_TIER_OPTIONS.map((opt) => (
          <label key={opt.value} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
            <input
              type="radio"
              name="firm_tier"
              checked={tier === opt.value}
              onChange={() => update({ firm_tier: opt.value })}
              className="accent-green-700"
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}

function SchoolTierSection({ draft, update }: { draft: SearchParams; update: (patch: Partial<SearchParams>) => void }) {
  const tier = draft.law_school_tier;

  return (
    <div>
      <SectionHeader label="Law School Tier" />
      <div className="flex gap-4">
        <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
          <input
            type="radio"
            name="school_tier"
            checked={tier == null}
            onChange={() => update({ law_school_tier: null })}
            className="accent-green-700"
          />
          Any
        </label>
        {SCHOOL_TIER_OPTIONS.map((opt) => (
          <label key={opt.value} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
            <input
              type="radio"
              name="school_tier"
              checked={tier === opt.value}
              onChange={() => update({ law_school_tier: opt.value })}
              className="accent-green-700"
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}

function HonorsSection({ draft, update }: { draft: SearchParams; update: (patch: Partial<SearchParams>) => void }) {
  const selected = draft.honors ?? [];

  function toggle(h: string) {
    const next = selected.includes(h) ? selected.filter((x) => x !== h) : [...selected, h];
    update({ honors: next });
  }

  return (
    <div>
      <SectionHeader label="Honors" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
        {HONORS.map((h) => (
          <label key={h} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
            <input type="checkbox" checked={selected.includes(h)} onChange={() => toggle(h)} className="rounded border-gray-300 accent-green-700" />
            {h}
          </label>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

export default function FilterModal({ open, searchParams, onSave, onClose }: Props) {
  const [draft, setDraft] = useState<SearchParams>(searchParams);

  useEffect(() => {
    if (open) setDraft(searchParams);
  }, [open, searchParams]);

  function update(patch: Partial<SearchParams>) {
    setDraft((prev) => ({ ...prev, ...patch }));
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-10 pb-10 px-4 bg-black/40 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Search Filters</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6 max-h-[72vh] overflow-y-auto">
          <LocationsSection  draft={draft} update={update} />
          <PracticeAreaSection draft={draft} update={update} />
          <SpecialtiesSection draft={draft} update={update} />
          <KeywordSection    draft={draft} update={update} />
          <CandidateTypesSection draft={draft} update={update} />
          <JdYearSection     draft={draft} update={update} />
          <TenureSection     draft={draft} update={update} />
          <FirmTierSection   draft={draft} update={update} />
          <SchoolTierSection draft={draft} update={update} />
          <HonorsSection     draft={draft} update={update} />
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-200">
          <Button onClick={() => onSave(draft)} className="bg-green-700 hover:bg-green-800 text-white">
            Save Filters
          </Button>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
